<template>
    <div class="buttons has-addons mb-0">
        <b-button
            :size="size"
            :loading="opening"
            :disabled="disabled"
            icon-left="file-pdf-box"
            :label="label || $t('PDF')"
            @click="open"
        />
        <b-dropdown position="is-bottom-left" append-to-body :disabled="disabled">
            <template #trigger>
                <b-button
                    :size="size"
                    :loading="downloading"
                    icon-left="chevron-down"
                    :title="$t('Download PDF')"
                    style="border-top-left-radius: 0; border-bottom-left-radius: 0; border-left: 0"
                />
            </template>
            <b-dropdown-item @click="download">
                <b-icon icon="download" size="is-small" class="mr-1" />
                {{ $t('Download PDF') }}
            </b-dropdown-item>
        </b-dropdown>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import openPdf, { downloadPdf } from '@/utils/OpenPdf';

/**
 * The house PDF export button: the main part opens the PDF in the browser's viewer
 * (a new tab), the arrow downloads it directly. See the `/pdf-export` skill.
 */
const props = defineProps({
    // Absolute API url of the PDF endpoint
    url: { type: String, required: true },
    filename: { type: String, default: 'document.pdf' },
    params: { type: Object, default: () => ({}) },
    // Extra axios config — the portal passes its Bearer header here
    config: { type: Object, default: () => ({}) },
    label: { type: String, default: '' },
    size: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    // @error listener: the caller reports the failure itself (the portal's toasts)
    // instead of the app-wide AxiosError handling
    onError: { type: Function, default: null }
});

const opening = ref(false);
const downloading = ref(false);

async function run(flag, action) {
    if (flag.value) return;

    flag.value = true;
    try {
        await action(props.url, props.params, props.filename, props.config);
    } catch (error) {
        if (props.onError) {
            props.onError(error);
        } else if (error.response) {
            new AxiosError(error.response).manage();
        }
    } finally {
        flag.value = false;
    }
}

const open = () => run(opening, openPdf);
const download = () => run(downloading, downloadPdf);
</script>
